// Client-side equivalent of Commonmodel::fetchGeoLocation()/locate_user() in the
// PHP app. There is no backend here, so we call a CORS-enabled IP geolocation
// API directly from the browser and map the country to a pricing currency
// using a static table (React equivalent of the gfv4_masterdata Country rows).
const CACHE_KEY = 'gf_visitor_location';
const CACHE_TTL_MS = 2 * 60 * 60 * 1000; // ~2 hours, mirrors the PHP session lifetime (sess_expiration = 7200)

// Countries not listed below default to USD / North America, same as the
// PHP view's default branch and Commonmodel's fallback-to-US behavior.
const EURO_COUNTRIES = new Set([
  'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR', 'HU', 'IE', 'IT',
  'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE', 'CH', 'NO', 'GB',
]);
const AUD_COUNTRIES = new Set(['AU', 'NZ']);

const CURRENCY_META = {
  usd: { zone: 'North America' },
  euro: { zone: 'Europe' },
  aud: { zone: 'Australia & NZ' },
};

// Header flag regions, mirroring navmenu.php's <img src="country_flag">.
// India gets its own flag even though it prices in USD like the rest of the
// "other" bucket - everything else outside Europe/Australia falls back to
// the USA flag, as requested.
const FLAG_META = {
  india: '/assets/images/in_64.png',
  usa: '/assets/images/united-states-flag-emblem-free-vector.jpg',
  europe: '/assets/images/flag-Euro.png',
  australia: '/assets/images/australia_flag.jpg',
};

const currencyForCountryCode = (countryCode = '') => {
  const code = countryCode.toUpperCase();
  if (AUD_COUNTRIES.has(code)) return 'aud';
  if (EURO_COUNTRIES.has(code)) return 'euro';
  return 'usd';
};

const flagRegionForCountryCode = (countryCode = '', currencyType) => {
  const code = countryCode.toUpperCase();
  if (code === 'IN') return 'india';
  if (currencyType === 'aud') return 'australia';
  if (currencyType === 'euro') return 'europe';
  return 'usa';
};

const buildLocation = (countryCode, countryName, ip) => {
  const currencyType = currencyForCountryCode(countryCode);
  const flagRegion = flagRegionForCountryCode(countryCode, currencyType);
  return {
    countryCode,
    countryName,
    currencyType,
    flagRegion,
    flag: FLAG_META[flagRegion],
    ip: ip || null,
    ...CURRENCY_META[currencyType],
  };
};

export const DEFAULT_LOCATION = buildLocation('US', 'United States', null);

const readCache = () => {
  try {
    const raw = window.localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const cached = JSON.parse(raw);
    if (!cached || Date.now() - cached.timestamp > CACHE_TTL_MS) return null;
    return cached.data;
  } catch {
    return null;
  }
};

const writeCache = (data) => {
  try {
    window.localStorage.setItem(CACHE_KEY, JSON.stringify({ data, timestamp: Date.now() }));
  } catch {
    // localStorage unavailable (private browsing, quota) - safe to ignore, just re-fetches next time
  }
};

// NOTE: ipapi.co's free tier is used here because it works directly from the
// browser over HTTPS with no API key. Unlike the PHP app, a React bundle can't
// keep a secret API key server-side (it would ship in the JS bundle and be
// visible in the network tab), so any keyed provider (e.g. ipgeolocation.io)
// should only be called from a serverless function if one is added later.
const GEO_API_URL = 'https://ipapi.co/json/';

export async function getVisitorLocation() {
  const cached = readCache();
  if (cached) return cached;

  try {
    const response = await fetch(GEO_API_URL);
    if (!response.ok) throw new Error(`geolocation request failed: ${response.status}`);
    const data = await response.json();
    if (data.error) throw new Error(data.reason || 'geolocation lookup failed');

    const location = buildLocation(data.country_code, data.country_name, data.ip);
    writeCache(location);
    return location;
  } catch {
    return DEFAULT_LOCATION;
  }
}
